import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Building2, Plus, Search } from 'lucide-react';
import { formatRelativeDate, type Client } from '@oneup/shared';
import { clientsApi } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  ManagementCardGrid,
  ManagementFooterSummary,
  ManagementHeader,
  ManagementLoadingState,
  ManagementPage,
  ManagementTable,
  type ManagementActiveFilter,
} from '@/components/management';
import { MANAGEMENT_PAGES, usePersistentManagementView } from '@/platform/management-pages';
import { useUiStore } from '@/stores/ui';
import { WorkspaceSplitView } from '@/components/workspace';
import { OrganizationHomebase } from './OrganizationHomebase';
import { contextItem, pageContext, suggestion, useAlicePageContext } from '@/components/alice';
import { adminConfig } from '@/platform/config';

type StatusFilter = 'Active' | 'Archived' | 'All';

function parseStatusFilter(value: string | null): StatusFilter {
  return value === 'Archived' || value === 'All' ? value : 'Active';
}

export function ClientsView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clientId } = useParams<{ clientId: string }>();
  const status = parseStatusFilter(searchParams.get('status'));
  const search = searchParams.get('search') ?? '';
  const [viewMode, setViewMode] = usePersistentManagementView(
    'organizations',
    searchParams,
    setSearchParams,
  );
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);

  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => clientsApi.list(),
  });

  const clients = data?.items ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((client) => {
      if (status !== 'All' && client.status !== status) return false;
      if (!q) return true;
      return (
        client.name.toLowerCase().includes(q) || (client.notes ?? '').toLowerCase().includes(q)
      );
    });
  }, [clients, search, status]);

  const setStatus = useCallback(
    (value: StatusFilter) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value === 'Active') params.delete('status');
          else params.set('status', value);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value) params.set('search', value);
          else params.delete('search');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const activeClientId = clientId;
  const activeClient = activeClientId
    ? clients.find((client) => client.id === activeClientId)
    : undefined;
  const page = MANAGEMENT_PAGES.organizations;
  const activeFilters: ManagementActiveFilter[] = [];
  if (status !== 'Active') {
    activeFilters.push({ id: 'status', label: 'Status', value: status, onClear: () => setStatus('Active') });
  }
  if (search) {
    activeFilters.push({ id: 'search', label: 'Search', value: `"${search}"`, onClear: () => setSearch('') });
  }
  const closeClient = () => {
    const qs = searchParams.toString();
    navigate(qs ? `/clients?${qs}` : '/clients');
  };
  const openClient = useCallback(
    (id: string) => {
      const qs = searchParams.toString();
      navigate(qs ? `/clients/${id}?${qs}` : `/clients/${id}`);
    },
    [navigate, searchParams],
  );
  const selectedIndex = activeClientId
    ? filtered.findIndex((client) => client.id === activeClientId)
    : -1;
  const goToClientAt = useCallback(
    (index: number) => {
      const client = filtered[index];
      if (client) openClient(client.id);
    },
    [filtered, openClient],
  );
  const aliceContext = useMemo(
    () =>
      pageContext({
        portalKey: adminConfig.appId,
        pageKey: 'organizations',
        pageTitle: 'Organizations',
        recordType: activeClientId ? 'organization' : undefined,
        recordId: activeClientId,
        recordLabel: activeClient?.name ?? activeClientId,
        organizationId: activeClientId,
        organizationLabel: activeClient?.name ?? activeClientId,
        contextItems: [
          contextItem(
            'organizations-page',
            'page',
            'Organizations',
            'Client organization records, status, linked tasks, contacts, websites, and communications.',
          ),
          ...(activeClientId
            ? [
                contextItem(
                  activeClientId,
                  'organization',
                  activeClient?.name ?? activeClientId,
                  activeClient?.notes ?? activeClient?.status,
                ),
              ]
            : []),
        ],
        suggestedActions: activeClientId
          ? [
              suggestion('organization-account-brief', 'Prepare account brief', 'Summarize account status and linked work.'),
              suggestion('organization-client-check-in', 'Draft client check-in', 'Start a client check-in draft.', 'composer'),
              suggestion('organization-open-risks', 'Find open risks', 'Identify visible blockers or stale work.'),
            ]
          : [],
      }),
    [activeClient, activeClientId],
  );
  useAlicePageContext(aliceContext);

  if (isLoading) return <ManagementLoadingState metricCount={3} />;
  if (error) return <LoadError title="Couldn't load clients" error={error} />;

  return (
    <ManagementPage>
      <ManagementHeader
        title={page.title}
        description={page.description}
        source={page.source}
        search={{ value: search, onChange: setSearch, placeholder: 'Search company or notes' }}
        filters={[
          {
            id: 'status',
            label: 'Status',
            value: status,
            onChange: (value) => setStatus(value as StatusFilter),
            options: ['Active', 'Archived', 'All'].map((value) => ({ value, label: value })),
          },
        ]}
        activeFilters={activeFilters}
        primaryAction={{ label: 'Create Organization', icon: Plus, onClick: () => openCreateDrawer({ type: 'client' }) }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {activeClientId ? (
        <OrganizationHomebase
          clientId={activeClientId}
          onClose={closeClient}
          listTitle="Organizations"
          selectedIndex={selectedIndex}
          totalRecords={filtered.length}
          onPrevious={() => goToClientAt(selectedIndex - 1)}
          onNext={() => goToClientAt(selectedIndex + 1)}
          onOpenTask={(id) => navigate(`/tasks/${id}`)}
          onOpenWebsite={(id) => navigate(`/websites/${id}`)}
          onOpenUser={(id) => navigate(`/users/${id}`)}
        />
      ) : (
      <WorkspaceSplitView
        footer={
          <ManagementFooterSummary>
            Showing <span className="tabular-nums text-foreground">{filtered.length}</span>
            {clients.length !== filtered.length && (
              <>
                {' '}of <span className="tabular-nums">{clients.length}</span> loaded
              </>
            )}
          </ManagementFooterSummary>
        }
      >
      {viewMode === 'cards' ? (
      <ManagementCardGrid>
        {filtered.length === 0 && (
          <Card className="px-3 py-8 text-center text-sm text-muted-foreground">No clients match this view.</Card>
        )}
        {filtered.map((client) => (
          <Card key={client.id} onClick={() => openClient(client.id)} className={cn('cursor-pointer space-y-2 px-3 py-3', activeClientId === client.id && 'bg-primary/5')}>
            <div className="flex items-center gap-2"><Avatar name={client.name} imageUrl={client.logoUrl} /><div className="min-w-0"><div className="truncate font-semibold">{client.name}</div><div className="text-xs text-muted-foreground">{client.relationshipType ?? 'Client'}</div></div></div>
            <div className="flex items-center justify-between text-xs"><span>{client.status ? <Badge variant="muted">{client.status}</Badge> : <MutedDash />}</span><span className="text-muted-foreground">Open support: {client.openSupportCount ?? 0}</span></div>
            <div className="text-xs text-muted-foreground">{formatCurrency(client.hourlyRate, '/hr')} · {formatRelativeDate(client.lastModifiedAt)}</div>
          </Card>
        ))}
      </ManagementCardGrid>
      ) : (

      <ManagementTable>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
                <th className="px-3 py-2.5">Logo</th>
                <th className="px-3 py-2.5">Company</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Open Support</th>
                <th className="px-3 py-2.5">Hourly Rate</th>
                <th className="px-3 py-2.5">Last Modified</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center text-sm text-muted-foreground">
                    No clients match this view.
                  </td>
                </tr>
              )}
              {filtered.map((client) => (
                <tr
                  key={client.id}
                  onClick={() => openClient(client.id)}
                  tabIndex={0}
                  role="button"
                  className={cn(
                    'cursor-pointer border-t border-border transition-colors hover:bg-muted/30 focus-visible:bg-muted/40 focus-visible:outline-none',
                    activeClientId === client.id && 'bg-primary/5',
                  )}
                >
                  <td className="px-3 py-2.5">
                    <Avatar name={client.name} imageUrl={client.logoUrl} />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-foreground">{client.name}</div>
                    {client.relationshipType && (
                      <div className="text-[11px] text-muted-foreground">
                        {client.relationshipType}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {client.status ? <Badge variant="muted">{client.status}</Badge> : <MutedDash />}
                  </td>
                  <td
                    className={cn(
                      'px-3 py-2.5 tabular-nums',
                      (client.openSupportCount ?? 0) === 0 && 'text-muted-foreground',
                    )}
                  >
                    {client.openSupportCount ?? 0}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {formatCurrency(client.hourlyRate, '/hr')}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {formatRelativeDate(client.lastModifiedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ManagementTable>
      )}
      </WorkspaceSplitView>
      )}
    </ManagementPage>
  );
}

export function FilterChips({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const active = option === value;
        return (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={cn(
              'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors',
              active
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-card text-muted-foreground hover:border-foreground/20 hover:text-foreground',
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-full sm:w-[280px]">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-8 pl-8 text-[13px]"
      />
    </div>
  );
}

export function Avatar({ name, imageUrl }: { name: string; imageUrl?: string }) {
  if (imageUrl) return <img src={imageUrl} alt="" className="h-8 w-8 rounded-md object-cover" />;
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-[11px] font-semibold text-primary">
      {initials(name) || <Building2 className="h-3.5 w-3.5" aria-hidden />}
    </div>
  );
}

export function LoadError({ title, error }: { title: string; error: unknown }) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" aria-hidden />
        <h2 className="text-base font-semibold">{title}</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </Card>
  );
}

export function MutedDash() {
  return <span className="text-muted-foreground">-</span>;
}

export function formatCurrency(value: number | undefined, suffix = '') {
  if (value === undefined) return '-';
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}
